/**
 * Content / SEO locales for the marketing site and multilingual blog.
 * New languages are added here without schema migrations (locale is a String).
 */

export const CONTENT_LOCALES = [
    'ru-RU',
    'ru-KZ',
    'kk-KZ',
    'uz-UZ',
    'az-AZ',
    'en',
] as const

export type ContentLocale = (typeof CONTENT_LOCALES)[number]

/** UI message packs (next-intl JSON files under messages/). */
export type UiMessageLocale = 'ru' | 'en' | 'kk' | 'uz' | 'az'

export interface ContentLocaleConfig {
    code: ContentLocale
    /** URL path prefix without trailing slash, e.g. /kz/kk */
    prefix: string
    /** BCP 47 for <html lang> (region is carried by URL + hreflang) */
    htmlLang: string
    /** Google hreflang value */
    hreflang: string
    /** Open Graph og:locale */
    ogLocale: string
    /** Which messages/*.json pack to load for chrome UI */
    uiMessages: UiMessageLocale
    /** Short label for switchers */
    label: string
    /** Extra translation prompt rules for this locale */
    promptHint?: string
}

export const CONTENT_LOCALE_CONFIG: Record<
    ContentLocale,
    ContentLocaleConfig
> = {
    'ru-RU': {
        code: 'ru-RU',
        prefix: '/ru',
        htmlLang: 'ru',
        hreflang: 'ru-RU',
        ogLocale: 'ru_RU',
        uiMessages: 'ru',
        label: 'RU',
    },
    'ru-KZ': {
        code: 'ru-KZ',
        prefix: '/kz',
        htmlLang: 'ru',
        hreflang: 'ru-KZ',
        ogLocale: 'ru_KZ',
        uiMessages: 'ru',
        label: 'KZ',
        promptHint:
            'Русский язык для аудитории Казахстана. Не использовать российские реалии там, где нужен казахстанский контекст.',
    },
    'kk-KZ': {
        code: 'kk-KZ',
        prefix: '/kz/kk',
        htmlLang: 'kk',
        hreflang: 'kk-KZ',
        ogLocale: 'kk_KZ',
        uiMessages: 'kk',
        label: 'KK',
        promptHint:
            'Современный литературный казахский язык, естественный для пользователей Казахстана. Не делать кальку с русского.',
    },
    'uz-UZ': {
        code: 'uz-UZ',
        prefix: '/uz',
        htmlLang: 'uz',
        hreflang: 'uz-UZ',
        ogLocale: 'uz_UZ',
        uiMessages: 'uz',
        label: 'UZ',
    },
    'az-AZ': {
        code: 'az-AZ',
        prefix: '/az',
        htmlLang: 'az',
        hreflang: 'az-AZ',
        ogLocale: 'az_AZ',
        uiMessages: 'az',
        label: 'AZ',
        promptHint:
            'Современный литературный азербайджанский язык, естественный для аудитории Азербайджана.',
    },
    en: {
        code: 'en',
        prefix: '/en',
        htmlLang: 'en',
        hreflang: 'en',
        ogLocale: 'en_US',
        uiMessages: 'en',
        label: 'EN',
    },
}

export const DEFAULT_CONTENT_LOCALE: ContentLocale = 'ru-RU'
export const X_DEFAULT_LOCALE: ContentLocale = 'en'

/** Current translation prompt version — bump to find outdated AI translations. */
export const TRANSLATION_PROMPT_VERSION = 'v1'

export const CONTENT_LOCALE_PREFIXES: Record<ContentLocale, string> =
    Object.fromEntries(
        CONTENT_LOCALES.map(code => [code, CONTENT_LOCALE_CONFIG[code].prefix])
    ) as Record<ContentLocale, string>

export function isContentLocale(value: string): value is ContentLocale {
    return (CONTENT_LOCALES as readonly string[]).includes(value)
}

/**
 * Accepts a routing locale (`ru-RU`), a URL prefix (`ru`, `/kz/kk`)
 * or an unknown segment (`manage`) and returns a known content locale.
 */
export function resolveContentLocale(
    value: string | null | undefined
): ContentLocale {
    if (value && isContentLocale(value)) {
        return value
    }

    if (value) {
        const prefix = value.startsWith('/') ? value : `/${value}`
        const match = CONTENT_LOCALES.find(
            code => CONTENT_LOCALE_CONFIG[code].prefix === prefix
        )
        if (match) {
            return match
        }
    }

    return DEFAULT_CONTENT_LOCALE
}

export function getContentLocaleConfig(locale: ContentLocale): ContentLocaleConfig {
    return CONTENT_LOCALE_CONFIG[locale]
}

export function getUiMessageLocale(locale: ContentLocale): UiMessageLocale {
    return CONTENT_LOCALE_CONFIG[locale].uiMessages
}

/** Sitemap bucket key for a content locale (kz covers both /kz and /kz/kk). */
export function getSitemapBucket(locale: ContentLocale): string {
    switch (locale) {
        case 'ru-RU':
            return 'ru'
        case 'ru-KZ':
        case 'kk-KZ':
            return 'kz'
        case 'uz-UZ':
            return 'uz'
        case 'az-AZ':
            return 'az'
        case 'en':
            return 'en'
    }
}

export const SITEMAP_BUCKETS = ['ru', 'kz', 'uz', 'az', 'en'] as const
export type SitemapBucket = (typeof SITEMAP_BUCKETS)[number]

export function localesForSitemapBucket(
    bucket: SitemapBucket
): ContentLocale[] {
    return CONTENT_LOCALES.filter(code => getSitemapBucket(code) === bucket)
}

/** dayjs / Intl locale hint from content locale */
export function getIntlLocale(locale: ContentLocale): string {
    switch (locale) {
        case 'en':
            return 'en-US'
        case 'kk-KZ':
            return 'kk-KZ'
        case 'uz-UZ':
            return 'uz-UZ'
        case 'az-AZ':
            return 'az-AZ'
        default:
            return 'ru-RU'
    }
}

export function getDayjsLocale(locale: ContentLocale): string {
    return locale === 'en' ? 'en' : 'ru'
}
